import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import DestinationsManager from './components/DestinationsManager';
import HotelsManager from './components/HotelsManager';
import TransportManager from './components/TransportManager';
import ActivitiesManager from './components/ActivitiesManager';
import RestaurantsManager from './components/RestaurantsManager';
import AttractionsManager from './components/AttractionsManager';
import PerformanceMetrics from './components/PerformanceMetrics';
import PopularDestinations from './components/PopularDestinations';
import UserEngagement from './components/UserEngagement';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('destinations');
  const [searchQuery, setSearchQuery] = useState('');

  // Admin access control
  useEffect(() => {
    if (!loading && !profileLoading) {
      if (!user) {
        navigate('/');
      } else if (userProfile && userProfile?.role !== 'admin') {
        navigate('/');
      }
    }
  }, [user, userProfile, loading, profileLoading, navigate]);

  // Show loading state while checking authentication
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if not admin
  if (!user || !userProfile || userProfile?.role !== 'admin') {
    return null;
  }

  const tabs = [
    { id: 'performance', label: 'Performance', icon: 'TrendingUp', count: 0 },
    { id: 'destinations', label: 'Destinations', icon: 'MapPin', count: 0 },
    { id: 'hotels', label: 'Hotels', icon: 'Building2', count: 0 },
    { id: 'transport', label: 'Transportation', icon: 'Plane', count: 0 },
    { id: 'activities', label: 'Activities', icon: 'Zap', count: 0 },
    { id: 'restaurants', label: 'Restaurants', icon: 'Utensils', count: 0 },
    { id: 'attractions', label: 'Attractions', icon: 'Camera', count: 0 },
  ];

  const renderActiveManager = () => {
    const props = { searchQuery };
    
    switch (activeTab) {
      case 'performance':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking & Revenue Analytics</h2>
              <p className="text-gray-600 mb-6">Track booking trends, revenue metrics, and financial performance</p>
              <PerformanceMetrics />
            </div>
            
            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Popular Destinations</h2>
              <p className="text-gray-600 mb-6">Analyze destination popularity, types, and booking patterns</p>
              <PopularDestinations />
            </div>
            
            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">User Engagement Metrics</h2>
              <p className="text-gray-600 mb-6">Monitor user activity, engagement rates, and retention trends</p>
              <UserEngagement />
            </div>
          </div>
        );
      case 'destinations':
        return <DestinationsManager {...props} />;
      case 'hotels':
        return <HotelsManager {...props} />;
      case 'transport':
        return <TransportManager {...props} />;
      case 'activities':
        return <ActivitiesManager {...props} />;
      case 'restaurants':
        return <RestaurantsManager {...props} />;
      case 'attractions':
        return <AttractionsManager {...props} />;
      default:
        return <PerformanceMetrics />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8 mt-20">
        {/* Dashboard Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Admin Dashboard
              </h1>
              <p className="text-gray-600">
                Manage travel inventory and content for LetsTravel Philippines
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Download"
                onClick={() => console.log('Export data')}
              >
                Export Data
              </Button>
              <Button
                variant="outline"
                iconName="Upload"
                onClick={() => console.log('Import data')}
              >
                Import Data
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-md">
            <Icon
              name="Search"
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <Input
              type="text"
              placeholder="Search across all modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === tab?.id
                    ? 'border-teal-500 text-teal-600 bg-teal-50' :'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Manager Component */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {renderActiveManager()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;